/**
 * @architecture-header 1
 * @layer shared
 * @domain visual-language
 * @responsibility Render the `.vpg` page skeleton, breadcrumb trail and back control.
 * @state-owner none
 * @allowed-imports React; shared icons
 * @forbidden business rules; network
 * @public-api workspace shell and modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { Fragment, type ReactNode } from 'react';
import { BackIcon } from './VispoIcon';

/** The page container with its header and body regions. */
export function Page({ children, maxWidth }: { children: ReactNode; maxWidth?: number | 'none' }) {
  return (
    <div className="vpg" style={maxWidth != null ? { maxWidth } : undefined}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  meta,
  actions,
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="vpghd">
      <h1>{title}</h1>
      {meta != null ? <span className="vpgm pgm">{meta}</span> : null}
      <div className="sp" />
      {actions ?? null}
    </div>
  );
}

export function PageBody({ children }: { children: ReactNode }) {
  return <div className="vpgb">{children}</div>;
}

export function GroupHeading({ children, count }: { children: ReactNode; count?: ReactNode }) {
  return (
    <div className="vgrph">
      {children}
      {count != null ? <span className="c2">{count}</span> : null}
    </div>
  );
}

export interface CrumbPart {
  readonly label: string;
  readonly onSelect?: () => void;
}

export function Breadcrumb({ parts }: { parts: readonly (CrumbPart | null)[] }) {
  const items = parts.filter((part): part is CrumbPart => Boolean(part));
  return (
    <>
      {items.map((part, index) => {
        const last = index === items.length - 1;
        return (
          <Fragment key={part.label}>
            {index > 0 ? <i>›</i> : null}
            {last ? (
              <b>{part.label}</b>
            ) : part.onSelect ? (
              <button className="cx" type="button" onClick={part.onSelect}>
                {part.label}
              </button>
            ) : (
              part.label
            )}
          </Fragment>
        );
      })}
    </>
  );
}

export function BackButton({ title, onSelect }: { title: string; onSelect: () => void }) {
  return (
    <button aria-label={title} className="bk" title={title} type="button" onClick={onSelect}>
      <BackIcon />
    </button>
  );
}

/** The narrow page container a few detail screens still use. */
export function WrapPage({ children, maxWidth }: { children: ReactNode; maxWidth?: number }) {
  return (
    <div className="wrap" style={maxWidth ? { maxWidth } : undefined}>
      {children}
    </div>
  );
}
